import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { applicationStore, deepCopy, isMobile, parseNumberByPrecision, userInfoStore } from '@pascal/pascal-core';
import { betStore, freeBetStore, gameConfigStore } from '@pascal/pascal-core/shared/core/store';

import { Range } from '../range';
import { RangeBtnValues } from '../rangeBtnValues';
import { resizeGameRange } from '../../../helpers';
import { doBetStore, gameStore } from '../../../store';
import { BetTypeTypeIndexes, countMaxNumberByMargin, countNumber, getOdd } from '../../../helpers/getOdd';

const staticRangeBtnValues = [
  {
    id: 1,
    from: 5,
    to: 9,
    selected: true,
    isDot: false,
  },
  {
    id: 2,
    from: 10,
    to: 14,
    selected: false,
    isDot: false,
    isLast: false,
  },
  {
    id: 3,
    from: 15,
    to: 20,
    selected: false,
    isDot: false,
    isLast: false,
  },
  {
    id: 4,
    from: 21,
    to: 25,
    selected: false,
    isDot: false,
    isLast: false,
  },
  {
    id: 5,
    from: 26,
    to: 30,
    selected: false,
    isDot: false,
    isLast: false,
  },
];
const minOdd = 1.01;
const RANGE_ID = 2;
let minOddOverTakenTimeOutId = null;

const getBoundingClientRect = (element) => {
  const rect = element.getBoundingClientRect();
  return {
    left: Math.ceil(rect.left),
    width: Math.ceil(rect.width),
    x: rect.x,
  };
};

export const BetTypeSectionBottomRangeAndBtn = observer(() => {
  const { margin, minOddOverTaken, setMinOddOverTaken, fixNumber, finishedResult } = gameStore;
  const { activeFreeBet } = freeBetStore;
  const { precision } = userInfoStore;
  const { isDemo } = applicationStore;
  const {
    limits: { maxWin },
  } = gameConfigStore;
  const {
    betInfoThisMoment: { odd },
    setBetInfoThisMoment,
    setRangeValues,
    rangeValues,
    betData: { amount: betAmount },
  } = doBetStore;

  const { repliedBetData } = betStore;

  const [offsetX, setOffsetX] = useState(0);
  const rangeRef = useRef(null);
  const [selectedRange, setSelectedRange] = useState(rangeValues[0]?.id);
  const [circleType, setCircleType] = useState(null);
  const [validRangeValues, setValidRangeValues] = useState([...rangeValues]);

  const limits = useMemo(
    () => ({
      min: 5,
      max: 30,
      minValueBetween: 1,
    }),
    []
  );
  const [currentMin, setCurrentMin] = useState(rangeValues[0]?.from);
  const [currentMax, setCurrentMax] = useState(rangeValues[0]?.to);
  const [scale, setScale] = useState(1);
  const [scaleForFireFox, setScaleForFireFox] = useState(1);
  const [sliderWidth, setSliderWidth] = useState(null);
  const [offsetSliderWidth, setOffsetSliderWidth] = useState(null);

  const winNumber = useMemo(() => {
    if (finishedResult) {
      return finishedResult.reduce((acc, current) => acc + current);
    }

    return fixNumber;
  }, [fixNumber, finishedResult]);

  useEffect(() => {
    const boundingClientRect = getBoundingClientRect(rangeRef?.current);
    setSliderWidth(boundingClientRect.width * scaleForFireFox);
    setOffsetSliderWidth(boundingClientRect.left * scaleForFireFox);
  }, [rangeRef.current, scaleForFireFox]);

  const windowResizer = () => {
    const resizedValue = resizeGameRange(true);
    setScale(resizedValue);
    if (navigator.userAgent.toLowerCase().indexOf('firefox') > -1) {
      setScaleForFireFox(resizedValue);
    }
  };

  useEffect(() => {
    if (!isMobile()) {
      windowResizer();
      window.addEventListener('resize', windowResizer);
    }
    return () => {
      if (isMobile()) {
        window.removeEventListener('resize', windowResizer);
      }
    };
  }, []);

  const mergeRangeByMaxMin = (rangeVal) => {
    const rangesResult = [];
    const ids = [];
    const rangesArr = deepCopy(rangeVal)
      .sort((a, b) => a.from - b.from)
      .map((item, index) => ({ ...item, id: index + 1 }));
    if (rangeVal.length > 1) {
      for (let i = 0; i < rangesArr.length - 1; i++) {
        const result = mergeFunc(rangesArr[i], rangesArr[i + 1]);
        if (result) {
          const findIndex = rangesResult.findIndex((item) => item.id === result.id);
          if (findIndex > -1) {
            rangesResult.splice(findIndex, 1, result);
          } else {
            ids.push(rangesArr[i].id, rangesArr[i + 1].id);
            rangesResult.push(result);
          }
        } else {
          if (!ids.includes(rangesArr[i].id)) {
            ids.push(rangesArr[i].id);
            rangesResult.push(rangesArr[i]);
          }
          if (!ids.includes(rangesArr[i + 1].id)) {
            ids.push(rangesArr[i + 1].id);
            rangesResult.push(rangesArr[i + 1]);
          }
        }
      }
    } else {
      return rangesArr;
    }
    return rangesResult;
  };

  const mergeFunc = (range1, range2) => {
    if (range1.to < range2.from && Math.abs(range1.to - range2.from) > 1) {
      return null;
    }
    return {
      ...range1,
      from: range1.from < range2.from ? range1.from : range2.from,
      to: range2.to > range1.to ? range2.to : range1.to,
    };
  };

  useEffect(() => {
    const validRanges = mergeRangeByMaxMin(rangeValues);
    const oddData = getOdd(margin, validRanges, precision || 2);

    if (oddData < minOdd) {
      setMinOddOverTaken(true);
      minOddOverTakenTimeOutId = setTimeout(() => {
        setMinOddOverTaken(false);
        minOddOverTakenTimeOutId = null;
      }, 0);
    }
    const payout = parseNumberByPrecision(
      oddData,
      activeFreeBet?.id && !isDemo ? activeFreeBet.amount : betAmount || 0,
      precision || 2
    );

    if (!repliedBetData) {
      setBetInfoThisMoment({
        odd: oddData,
        payout: payout > maxWin ? maxWin : payout,
      });
    }
  }, [rangeValues, betAmount, margin, activeFreeBet.id, odd, isDemo, repliedBetData]);

  const changeRangeSelected = (selectedId, from, to) => {
    const findRangeIndex = rangeValues.findIndex((item) => item.from <= from && item.to >= to);
    const innerRanges = rangeValues.filter((item) => item.from >= from && item.to < to);
    if (innerRanges.length) {
      innerRanges.forEach((item) => {
        const index = rangeValues.findIndex((range) => range.id === item.id);
        rangeValues.splice(index, 1);
      });
    }
    if (findRangeIndex > -1) {
      const ranges = [...rangeValues];
      const filterRanges = rangeValues.filter((item) => !(item.from === from && item.to === to));
      const index = rangeValues.findIndex((item) => item.from === from && item.to === to);
      if (index > -1) {
        setRangeValues(
          filterRanges
            .sort((_a, b) => _a.from - b.from)
            .map((item, _index) => ({
              ...item,
              id: _index + 1,
              isDot: item.from === item.to,
            }))
        );
      } else {
        const findRange = ranges[findRangeIndex];
        const findIn = ranges.findIndex((item) => item.from < from && item.to > to);
        if (findIn > -1) {
          const lastResult = ranges.splice(findIn, 1)[0];
          ranges.push({
            ...lastResult,
            isDot: false,
            from: lastResult.from,
            to: from - 1,
          });
          ranges.push({
            ...lastResult,
            isDot: false,
            from: to + 1,
            to: lastResult.to,
          });
        } else {
          ranges.splice(findRangeIndex, 1, {
            ...ranges[findRangeIndex],
            isDot: false,
            from: findRange.from === from ? to + 1 : findRange.to === to ? findRange.from : from,
            to:
              findRange.to === to
                ? findRange.to - (to - from === 5 ? 6 : 5)
                : findRange.from === from
                ? findRange.to
                : to,
          });
        }
        setRangeValues(
          ranges
            .sort((_a, b) => _a.from - b.from)
            .map((item, _index) => ({
              ...item,
              id: _index + 1,
              isDot: item.from === item.to,
            }))
        );
      }
    } else {
      let finalRanges = [...rangeValues];
      const findBeforRandeIndex = finalRanges.findIndex((item) => item.from <= from && item.to >= from);
      const findAfterRandeIndex = finalRanges.findIndex((item) => to >= item.from && to <= item.to);
      const staticRange = deepCopy(staticRangeBtnValues.find((item) => item.id === selectedId));
      let checkedRange;
      const rangeIndexByStaticStart = rangeValues.findIndex(
        (item) => item.from <= staticRange.from && item.to >= staticRange.from
      );
      const rangeIndexByStaticEnd = rangeValues.findIndex(
        (item) => item.from <= staticRange.to && item.to >= staticRange.to
      );
      if (findBeforRandeIndex > -1 && findAfterRandeIndex < 0) {
        checkedRange = { ...rangeValues[rangeIndexByStaticStart] };
        checkedRange.to = checkedRange.to > staticRange.to ? checkedRange.to : staticRange.to;
        checkedRange.from = checkedRange.from >= staticRange.from ? staticRange.from : checkedRange.from;
        const outherRanges = finalRanges.filter(
          (range) => range.to < checkedRange.from || range.from > checkedRange.to
        );
        const oddByNumberCount = getOdd(margin, [checkedRange, ...outherRanges], precision);
        if (oddByNumberCount >= minOdd) {
          finalRanges.splice(rangeIndexByStaticStart, 1, checkedRange);
        } else {
          setMinOddOverTaken(true);
          minOddOverTakenTimeOutId = setTimeout(() => {
            setMinOddOverTaken(false);
            minOddOverTakenTimeOutId = null;
          }, 500);
        }
      }
      if (findAfterRandeIndex > -1 && findBeforRandeIndex < 0) {
        checkedRange = {
          ...finalRanges[rangeIndexByStaticEnd],
          isDot: false,
        };
        checkedRange.from = checkedRange.from >= staticRange.from ? staticRange.from : checkedRange.from;
        const outherRanges = finalRanges.filter(
          (range) => range.to < checkedRange.from || range.from > checkedRange.to
        );
        const oddByNumberCount = getOdd(margin, [checkedRange, ...outherRanges], precision);
        if (oddByNumberCount >= minOdd) {
          finalRanges.splice(rangeIndexByStaticEnd, 1, checkedRange);
          finalRanges = finalRanges
            .sort((a, b) => a.from - b.from)
            .map((item, index) => ({
              ...item,
              id: index + 1,
            }));
        } else {
          setMinOddOverTaken(true);
          minOddOverTakenTimeOutId = setTimeout(() => {
            setMinOddOverTaken(false);
            minOddOverTakenTimeOutId = null;
          }, 500);
        }
      }
      if (findBeforRandeIndex > -1 && findAfterRandeIndex > -1) {
        const selectedRangeCopy = {
          ...staticRange,
          isDot: false,
        };
        selectedRangeCopy.from =
          finalRanges[rangeIndexByStaticStart].from <= staticRange.from
            ? finalRanges[rangeIndexByStaticStart].from
            : staticRange.from;
        selectedRangeCopy.to =
          finalRanges[rangeIndexByStaticEnd].to >= staticRange.to
            ? finalRanges[rangeIndexByStaticEnd].to
            : staticRange.to;
        const outherRanges = finalRanges.filter(
          (range) => range.to < selectedRangeCopy.from || range.from > selectedRangeCopy.to
        );
        const id = outherRanges[outherRanges.length - 1]?.id;
        selectedRangeCopy.id = id ? id + 1 : 1;
        const oddByNumberCount = getOdd(margin, [selectedRangeCopy, ...outherRanges], precision);
        if (oddByNumberCount >= minOdd) {
          finalRanges.splice(rangeIndexByStaticStart, 1, selectedRangeCopy);
          finalRanges.splice(rangeIndexByStaticEnd, 1);
        } else {
          setMinOddOverTaken(true);
          minOddOverTakenTimeOutId = setTimeout(() => {
            setMinOddOverTaken(false);
            minOddOverTakenTimeOutId = null;
          }, 500);
        }
      }
      if (findBeforRandeIndex < 0 && findAfterRandeIndex < 0) {
        const result = {
          isDot: false,
          to,
          from,
        };
        finalRanges.push(result);
        finalRanges = finalRanges
          .sort((a, b) => a.from - b.from)
          .map((item, index) => ({
            ...item,
            id: index + 1,
          }));
      }
      const staticRangeObj = deepCopy(staticRangeBtnValues.find((item) => item.id === selectedId));
      const findRange = finalRanges.find((item) => item.from <= staticRangeObj.from && item.to >= staticRangeObj.to);
      let filterRanges;
      let mergeArr;
      if (findRange) {
        filterRanges = finalRanges.filter((item) => item.id !== findRange?.id);
        mergeArr = filterRanges.filter(
          (item) => Math.abs(item.to - findRange.from) === 1 || Math.abs(findRange.to - item.from) === 1
        );
      }
      const oddData = getOdd(margin, finalRanges, precision);
      if (oddData < minOdd) {
        setMinOddOverTaken(true);
        setTimeout(() => {
          setMinOddOverTaken(false);
        }, 500);
        finalRanges = finalRanges.filter((item) => item.id !== findRange?.id);
      } else if (mergeArr && mergeArr.length) {
        const idArr = mergeArr.map((item) => item.id);
        const mergeObj = {
          ...findRange,
          isDot: false,
        };
        finalRanges = filterRanges.filter((item) => !idArr.includes(item.id));
        mergeArr.forEach((item) => {
          mergeObj.from = item.from < mergeObj.from ? item.from : mergeObj.from;
          mergeObj.to = item.to < mergeObj.to ? mergeObj.to : item.to;
        });
        finalRanges.push(mergeObj);
      }
      const innerSmallRanges = innerRanges.filter(
        (item) => !finalRanges.some((range) => range.from <= item.from && range.to >= item.to)
      );
      finalRanges = [...finalRanges, ...innerSmallRanges];
      setRangeValues(
        finalRanges
          .sort((_a, b) => _a.from - b.from)
          .map((item, _index) => ({
            ...item,
            id: _index + 1,
            isDot: item.from === item.to,
          }))
      );
    }
  };

  const calculateOddForRanges = (ranges, selectRange) => {
    const count = countNumber(RANGE_ID, ranges);
    const selectedNumbersCount = countNumber(RANGE_ID, selectRange);
    const maxNumberCount = countMaxNumberByMargin(margin);
    const oddData = getOdd(margin, rangeValues, precision);
    return [count, selectedNumbersCount, maxNumberCount, oddData];
  };

  const mergeAllTypeRange = (tabIndex, rangeValuesArr, obj, type) => {
    if (type === 'dot') {
      mergeDot(rangeValuesArr, obj);
    } else if (tabIndex === BetTypeTypeIndexes.RANGE) {
      if (type === 'maxMin') {
        const validRangesResult = mergeRangeByMaxMin(
          rangeValues.map((item) => ({ ...item, isDot: item.from === item.to }))
        );
        const oddData = getOdd(margin, validRangesResult, precision);
        if (oddData >= minOdd) {
          setRangeValues(validRangesResult.map((item) => ({ ...item, isDot: item.from === item.to })));
        } else {
          setRangeValues(validRangeValues.map((item) => ({ ...item, isDot: item.from === item.to })));
        }
      } else {
        setRangeValues(
          mergeRange(tabIndex, rangeValuesArr, obj, type).map((item) => ({ ...item, isDot: item.from === item.to }))
        );
      }
    }
  };

  const mergeDot = (rangesArr, dot) => {
    let finalRanges = [...rangesArr];
    const oddData = getOdd(margin, mergeRangeByMaxMin([...finalRanges, dot]), precision);
    if (oddData < minOdd) {
      setMinOddOverTaken(true);
      setRangeValues(validRangeValues.map((item) => ({ ...item, isDot: item.from === item.to })));
      setTimeout(() => {
        setMinOddOverTaken(false);
      }, 500);
    } else {
      const findIndex = finalRanges.findIndex((item) =>
        item.isDot ? item.from === dot.from : item.from <= dot.from && item.to >= dot.from
      );
      if (findIndex < 0) {
        finalRanges.push(dot);
      } else if (finalRanges[findIndex].isDot) {
        finalRanges.splice(findIndex, 1, dot);
      }
      const { from } = dot;
      const filterRanges = finalRanges.filter((item) => item.id !== dot.id);
      const mergeArr = filterRanges.filter((item) => item.from - from === 1 || from - item.to === 1);
      let mergeDotObj;
      if (mergeArr.length) {
        const idArr = mergeArr.map((item) => item.id);
        mergeDotObj = {
          ...dot,
          isDot: false,
        };
        finalRanges = filterRanges.filter((item) => !idArr.includes(item.id));
        mergeArr.forEach((item) => {
          mergeDotObj.from = item.from < mergeDotObj.from ? item.from : mergeDotObj.from;
          mergeDotObj.to = item.to < mergeDotObj.to ? mergeDotObj.to : item.to;
        });
        finalRanges.push(mergeDotObj);
      }
      finalRanges = finalRanges
        .sort((a, b) => a.from - b.from)
        .map((item, index) => ({
          ...item,
          id: index + 1,
        }));
      setRangeValues(finalRanges);
    }
  };

  const calculateValidOdd = (lastMarket, allRanges, type) => {
    const selectedRange = {
      ...lastMarket,
      from: type === 'max' ? lastMarket.from : lastMarket.from + 1,
      to: type === 'max' ? lastMarket.to - 1 : lastMarket.to,
      isLast: false,
    };

    const oddData = getOdd(margin, mergeRangeByMaxMin([...allRanges, selectedRange]), precision);

    if (oddData >= minOdd) {
      return mergeRangeByMaxMin([...allRanges, selectedRange])
        .sort((a, b) => a.from - b.from)
        .map((item, index) => ({
          ...item,
          id: index + 1,
        }));
    }
    return calculateValidOdd(selectedRange, allRanges, type);
  };

  const mergeRange = (tabIndex, rangeValuesArr, obj, type) => {
    let ranges = [...rangeValuesArr];
    const clickedRange = { ...obj };
    let rangesOdd;
    const arrFromRanges = ranges.filter(
      (item) =>
        !(item.from <= clickedRange.to && clickedRange.to <= item.to) &&
        !(clickedRange.from >= item.from && clickedRange.from <= item.to)
    );
    const index = ranges.findIndex(
      (item) =>
        (item.from <= clickedRange.to && clickedRange.to <= item.to) ||
        (clickedRange.from >= item.from && clickedRange.from <= item.to)
    );
    if (index > -1) {
      const lastResultRange = {
        ...clickedRange,
        from: ranges[index].from < clickedRange.from ? ranges[index].from : clickedRange.from,
        to: ranges[index].to > clickedRange.to ? ranges[index].to : clickedRange.to,
      };
      const [count, selectedNumbersCount, maxNumberCount, oddData] = calculateOddForRanges(
        [...arrFromRanges, lastResultRange],
        [...arrFromRanges]
      );
      if (oddData < minOdd) {
        ranges = calculateValidOdd(clickedRange, rangeValuesArr, type);
      } else {
        ranges.splice(index, 1, lastResultRange);
      }
    } else {
      const [count, selectedNumbersCount, maxNumberCount, oddData] = calculateOddForRanges(
        [...arrFromRanges, obj],
        [...arrFromRanges]
      );
      rangesOdd = oddData;
      const result = [{ ...obj }];
      if (oddData < minOdd) {
        ranges = calculateValidOdd(clickedRange, rangeValuesArr, type);
      } else {
        ranges = [...arrFromRanges, ...result];
      }
    }
    const filterRanges = ranges.filter((item) => item?.id !== obj?.id);
    const mergeArr = filterRanges?.filter(
      (item) => Math.abs(item?.to - obj?.from) === 1 || Math.abs(obj?.to - item?.from) === 1
    );
    if (mergeArr.length && rangesOdd > minOdd) {
      const idArr = mergeArr.map((item) => item.id);
      const mergeObj = {
        ...obj,
        isDot: false,
      };
      ranges = filterRanges.filter((item) => !idArr.includes(item.id));
      mergeArr.forEach((item) => {
        mergeObj.from =
          item.from < mergeObj.from || Math.abs(item.from - mergeObj.from) === 1 ? item.from : mergeObj.from;
        mergeObj.to = item.to < mergeObj.to ? mergeObj.to : item.to;
      });
      ranges.push(mergeObj);
    }
    ranges = ranges
      .filter((item) => item.from)
      .sort((a, b) => a.from - b.from)
      .map((item, _index) => ({
        ...item,
        id: _index + 1,
        isDot: item.from === item.to,
      }));
    return ranges;
  };

  const onMouseUpMax = () => {
    setSelectedRange(null);
    setCircleType(null);
    clearTimeout(minOddOverTakenTimeOutId);
    minOddOverTakenTimeOutId = null;
    setMinOddOverTaken(false);
    const obj = rangeValues
      .map((_range) => ({
        ..._range,
        from: _range.from > _range.to ? _range.to : _range.from,
        to: _range.to > _range.from ? _range.to : _range.from,
      }))
      .find((item) => item.id === selectedRange);
    const arr = rangeValues
      .map((_range) => ({
        ..._range,
        from: _range.from > _range.to ? _range.to : _range.from,
        to: _range.to > _range.from ? _range.to : _range.from,
      }))
      .filter((item) => item.id !== selectedRange);
    mergeAllTypeRange(RANGE_ID, arr, obj, 'max');

    if (isMobile()) {
      document.removeEventListener('touchend', onMouseUpMax);
      document.removeEventListener('touchmove', onMouseMoveMax);
    } else {
      document.removeEventListener('mouseup', onMouseUpMax);
      document.removeEventListener('mousemove', onMouseMoveMax);
    }
  };

  const onMouseUpMin = () => {
    setSelectedRange(null);
    setCircleType(null);
    clearTimeout(minOddOverTakenTimeOutId);
    minOddOverTakenTimeOutId = null;
    setMinOddOverTaken(false);
    const obj = rangeValues
      .map((_range) => ({
        ..._range,
        from: _range.from > _range.to ? _range.to : _range.from,
        to: _range.to > _range.from ? _range.to : _range.from,
      }))
      .find((item) => item.id === selectedRange);
    const arr = rangeValues
      .map((_range) => ({
        ..._range,
        from: _range.from > _range.to ? _range.to : _range.from,
        to: _range.to > _range.from ? _range.to : _range.from,
      }))
      .filter((item) => item.id !== selectedRange);
    mergeAllTypeRange(RANGE_ID, arr, obj, 'min');

    if (isMobile()) {
      document.removeEventListener('touchend', onMouseUpMin);
      document.removeEventListener('touchmove', onMouseMoveMin);
    } else {
      document.removeEventListener('mouseup', onMouseUpMin);
      document.removeEventListener('mousemove', onMouseMoveMin);
    }
  };

  const onMouseMoveMax = useCallback(
    (e) => {
      const clientX = e?.clientX || e.touches?.[0]?.clientX;
      const { min, max } = limits;
      const draggedWidth = clientX * scaleForFireFox - offsetSliderWidth;
      const draggedWidthInPercent = (draggedWidth * 100) / sliderWidth;
      const currentMax1 = parseInt(((max - min + 1) * draggedWidthInPercent) / 100, 10) + min;
      const filterRanges = rangeValues.filter((item) => item.id !== selectedRange);
      const changedRange = rangeValues.find((item) => item.id === selectedRange);
      const rangeByFromValue = filterRanges.map((item) => item.from);
      const minFromValue = rangeByFromValue.find((item) => item > changedRange.from);
      const range = filterRanges.find((item) => item.from === minFromValue);
      const findRange = rangeValues.find((item) => item.id === selectedRange);
      const some = filterRanges.some((item) => item.to > changedRange.to);
      const bool = range?.to < changedRange.from ? true : currentMax1 < range?.to ? true : !some;
      const activeRange = currentMax1 >= findRange.from && currentMax1 <= max && bool;
      if (activeRange) {
        const arr = rangeValues
          .map((item) => {
            if (item.id === selectedRange) {
              item.to = currentMax1;
              item.isLast = true;
            }
            return {
              ...item,
              isLast: false,
            };
          })
          .map((_range) => ({
            ..._range,
            from: _range.from > _range.to ? _range.to : _range.from,
            to: _range.to > _range.from ? _range.to : _range.from,
          }));
        setRangeValues(
          arr
            .sort((a, b) => a.from - b.from)
            .map((item, _index) => {
              return {
                ...item,
                isDot: item.from === item.to,
              };
            })
        );
        setCurrentMax(currentMax1);
      }
    },
    [offsetSliderWidth, sliderWidth, currentMin, selectedRange, rangeValues]
  );

  const onMouseMoveMin = useCallback(
    (e) => {
      const { min, max } = limits;
      const clientX = e?.clientX || e.touches?.[0]?.clientX;
      const draggedWidth = clientX * scaleForFireFox - offsetSliderWidth;
      const draggedWidthInPercent = (draggedWidth * 100) / sliderWidth;
      const currentMin1 = parseInt(((max - min + 1) * draggedWidthInPercent) / 100, 10) + min;
      const findedRange = rangeValues.find((item) => item.id === selectedRange);
      const changedRangeIndex = rangeValues.findIndex((item) => item.id === selectedRange);
      const lastRange = rangeValues[changedRangeIndex - 1];
      const nextRange = rangeValues[changedRangeIndex + 1];
      let bool;
      if (nextRange && lastRange) {
        bool = lastRange?.from < currentMin1 && nextRange?.to > currentMin1;
      } else if (lastRange && !nextRange) {
        bool = lastRange?.from < currentMin1;
      } else if (!lastRange && nextRange) {
        bool = nextRange?.to > currentMin1;
      } else {
        bool = true;
      }
      const activeRange = currentMin1 >= min && currentMin1 <= findedRange.to && bool;
      if (activeRange) {
        const arr = rangeValues
          .map((item) => {
            if (item.id === selectedRange) {
              item.from = currentMin1;
              item.isLast = true;
            }
            return {
              ...item,
              isLast: false,
            };
          })
          .map((_range) => ({
            ..._range,
            from: _range.from > _range.to ? _range.to : _range.from,
            to: _range.to > _range.from ? _range.to : _range.from,
          }));
        setRangeValues(
          arr
            .sort((a, b) => a.from - b.from)
            .map((item, _index) => ({
              ...item,
              isDot: item.from === item.to,
            }))
        );
        setCurrentMin(currentMin1);
      }
    },
    [offsetSliderWidth, sliderWidth, currentMax, selectedRange, rangeValues]
  );

  const onMouseUpMaxMin = () => {
    setSelectedRange(null);
    setCircleType(null);
    clearTimeout(minOddOverTakenTimeOutId);
    minOddOverTakenTimeOutId = null;
    setMinOddOverTaken(false);
    const obj = rangeValues.find((item) => item.id === selectedRange);
    const arr = rangeValues.filter((item) => item.id !== selectedRange);
    mergeAllTypeRange(RANGE_ID, arr, obj, 'maxMin');

    if (isMobile()) {
      document.removeEventListener('touchmove', onMouseMoveMaxMin);
      document.removeEventListener('touchend', onMouseUpMaxMin);
    } else {
      document.removeEventListener('mousemove', onMouseMoveMaxMin);
      document.removeEventListener('mouseup', onMouseUpMaxMin);
    }
  };

  const onMouseMoveMaxMin = useCallback(
    (e) => {
      const { min, max } = limits;
      const clientX = e?.clientX || e.touches?.[0]?.clientX;
      const boundingClientRect = rangeRef?.current?.getBoundingClientRect();
      const sliderWidthActual = boundingClientRect.width;
      const offsetSliderWidthActual = boundingClientRect.left;
      const isFirefox = navigator.userAgent.toLowerCase().indexOf('firefox') > -1;
      let draggedWidthMin;
      if (isFirefox) {
        draggedWidthMin = clientX - offsetSliderWidthActual;
      } else {
        draggedWidthMin = (clientX - offsetSliderWidth) * scaleForFireFox;
      }
      const draggedWidthInPercentMin = (draggedWidthMin * 100) / (isFirefox ? sliderWidthActual : sliderWidth);

      const newMinValue = parseInt(((max - min + 1) * draggedWidthInPercentMin) / 100, 10) + min;

      const selectedRangeIndex = rangeValues.findIndex((item) => item.id === selectedRange);
      const selectedRangeObj = rangeValues[selectedRangeIndex];

      if (!selectedRangeObj) {
        // console.error('Selected range object is undefined.');
        return;
      }

      const lastRange = rangeValues[selectedRangeIndex - 1];
      const nextRange = rangeValues[selectedRangeIndex + 1];
      const rangeWidth = selectedRangeObj.to - selectedRangeObj.from;
      const centeredMinValue = newMinValue - Math.floor(rangeWidth / 2);

      let isValidRange = true;
      if (nextRange && lastRange) {
        isValidRange = lastRange?.from < centeredMinValue && nextRange?.to > centeredMinValue + rangeWidth;
      } else if (lastRange && !nextRange) {
        isValidRange = lastRange?.from < centeredMinValue;
      } else if (!lastRange && nextRange) {
        isValidRange = nextRange?.to > centeredMinValue + rangeWidth;
      }

      if (isFirefox) {
        isValidRange = isValidRange && centeredMinValue >= min && centeredMinValue + rangeWidth <= max;
      }

      if (centeredMinValue >= min && centeredMinValue + rangeWidth <= max && isValidRange) {
        const updatedRangeValues = rangeValues.map((item) => {
          if (item.id === selectedRange) {
            item.from = centeredMinValue < min ? min : centeredMinValue;
            item.to = item.from + rangeWidth;
            item.isLast = true;
          }
          return {
            ...item,
            isLast: false,
          };
        });

        setRangeValues(
          updatedRangeValues
            .sort((a, b) => a.from - b.from)
            .map((item, _index) => ({
              ...item,
            }))
        );
        setCurrentMin(centeredMinValue);
      }
    },
    [offsetSliderWidth, sliderWidth, currentMax, selectedRange, offsetX, rangeValues, scale, limits]
  );

  const onMouseMoveRangeDot = (e) => {
    const clientX = e?.clientX || e.touches?.[0]?.clientX;
    const { min, max } = limits;
    const draggedWidth = clientX * scaleForFireFox - offsetSliderWidth;
    const draggedWidthInPercent = (draggedWidth * 100) / sliderWidth;
    let currentDotCoordinate = parseInt(((max - min + 1) * draggedWidthInPercent) / 100, 10) + min;
    currentDotCoordinate = currentDotCoordinate > max ? max : currentDotCoordinate < min ? min : currentDotCoordinate;
    const changedRangeIndex = rangeValues.findIndex((item) => item.id === selectedRange);
    const changedRange = rangeValues[changedRangeIndex];
    const lastRange = rangeValues[changedRangeIndex - 1];
    const nextRange = rangeValues[changedRangeIndex + 1];
    let bool;
    if (nextRange && lastRange) {
      bool =
        lastRange?.from < currentDotCoordinate &&
        nextRange?.to > changedRange.to - changedRange.from + currentDotCoordinate &&
        currentDotCoordinate < nextRange?.to;
    } else if (lastRange && !nextRange) {
      bool = lastRange?.from < currentDotCoordinate;
    } else if (!lastRange && nextRange) {
      bool = nextRange?.to > changedRange.to - changedRange.from + currentDotCoordinate;
    } else {
      bool = true;
    }
    if (currentDotCoordinate >= min && currentDotCoordinate <= max && bool) {
      const ranges = rangeValues.map((item) => {
        if (item.id === selectedRange) {
          item.from = currentDotCoordinate;
          item.to = currentDotCoordinate;
          item.isDotChanged = true;
        }

        return item;
      });
      setRangeValues(ranges);
    }
  };

  const onMouseUpRangeDot = () => {
    setSelectedRange(null);
    setCircleType(null);
    clearTimeout(minOddOverTakenTimeOutId);
    minOddOverTakenTimeOutId = null;
    setMinOddOverTaken(false);
    let newParsedRangeValues = [...rangeValues];

    const findIndexResult = newParsedRangeValues.findIndex((item) => item.id === selectedRange && !item.isDotChanged);

    if (findIndexResult > -1) {
      newParsedRangeValues = newParsedRangeValues.filter((item) => item.id !== selectedRange);
    } else {
      newParsedRangeValues = newParsedRangeValues.map((item) => {
        if (item.isDotChanged) {
          item.isDotChanged = false;
        }

        return item;
      });
    }

    const obj = newParsedRangeValues.find((item) => item.id === selectedRange);
    const arr = newParsedRangeValues.filter((item) => item.id !== selectedRange);

    if (obj) {
      mergeAllTypeRange(RANGE_ID, arr, obj, 'dot');
    } else {
      setRangeValues(newParsedRangeValues);
    }

    if (isMobile()) {
      document.removeEventListener('touchmove', onMouseMoveRangeDot);
      document.removeEventListener('touchend', onMouseUpRangeDot);
    } else {
      document.removeEventListener('mousemove', onMouseMoveRangeDot);
      document.removeEventListener('mouseup', onMouseUpRangeDot);
    }
  };

  useEffect(() => {
    if (selectedRange !== null) {
      if (circleType === 'max') {
        if (isMobile()) {
          document.addEventListener('touchmove', onMouseMoveMax);
          document.addEventListener('touchend', onMouseUpMax);
        } else {
          document.addEventListener('mousemove', onMouseMoveMax);
          document.addEventListener('mouseup', onMouseUpMax);
        }
      } else if (circleType === 'min') {
        if (isMobile()) {
          document.addEventListener('touchmove', onMouseMoveMin);
          document.addEventListener('touchend', onMouseUpMin);
        } else {
          document.addEventListener('mousemove', onMouseMoveMin);
          document.addEventListener('mouseup', onMouseUpMin);
        }
      } else if (circleType === 'maxMin') {
        if (isMobile()) {
          document.addEventListener('touchmove', onMouseMoveMaxMin);
          document.addEventListener('touchend', onMouseUpMaxMin);
        } else {
          document.addEventListener('mousemove', onMouseMoveMaxMin);
          document.addEventListener('mouseup', onMouseUpMaxMin);
        }
      } else if (circleType === 'rangeDot') {
        if (isMobile()) {
          document.addEventListener('touchmove', onMouseMoveRangeDot);
          document.addEventListener('touchend', onMouseUpRangeDot);
        } else {
          document.addEventListener('mousemove', onMouseMoveRangeDot);
          document.addEventListener('mouseup', onMouseUpRangeDot);
        }
      }
    }
  }, [selectedRange, circleType]);

  const changeMinValue = (e) => {
    e.stopPropagation();
    setSelectedRange(+e.target.getAttribute('rangeid'));
    setCircleType('min');
  };

  const changeMaxValue = (e) => {
    e.stopPropagation();
    setSelectedRange(+e.target.getAttribute('rangeid'));
    setCircleType('max');
  };

  const changeMaxMinValue = (e) => {
    const clientX = e?.clientX || e.touches?.[0]?.clientX;
    setValidRangeValues(deepCopy(rangeValues));
    const range = Array.from(document.querySelectorAll('.w-md-bet-range')).find(
      (item) => +item.getAttribute('rangeid') === +e.target.getAttribute('rangeid')
    );
    setOffsetX(range?.offsetLeft - clientX);
    setSelectedRange(+e.target.getAttribute('rangeid'));
    setCircleType('maxMin');
  };

  const handleCurrentRangeDot = (e) => {
    const clientX = e?.clientX || e.touches?.[0]?.clientX;
    const { max, min } = limits;
    const draggedWidth = clientX * scaleForFireFox - offsetSliderWidth;
    const draggedWidthInPercent = (draggedWidth * 100) / sliderWidth;
    let currentDotCoordinate = parseInt(((max - min + 1) * draggedWidthInPercent) / 100, 10) + min;
    currentDotCoordinate = currentDotCoordinate > max ? max : currentDotCoordinate < min ? min : currentDotCoordinate;
    const range = {
      isDot: true,
      from: currentDotCoordinate,
      to: currentDotCoordinate,
    };
    let ranges = rangeValues;
    let odd;
    const comparedRangeIndex = ranges.findIndex((item) => range.from >= item.from && range.to <= item.to);
    if (comparedRangeIndex === -1) {
      const alreadySelectedIndex = ranges.findIndex((item) => item.from === range.from);
      if (alreadySelectedIndex === -1) {
        odd = getOdd(margin, [...ranges, range], precision);
        if (odd < minOdd) {
          setMinOddOverTaken(true);
          setTimeout(() => {
            setMinOddOverTaken(false);
          }, 500);
          ranges = [...ranges];
        } else {
          ranges = [range, ...ranges]
            .sort((a, b) => a.from - b.from)
            .map((item, index) => ({
              ...item,
              id: index + 1,
            }));
        }
      }
      ranges.splice(alreadySelectedIndex, 0);
    }
    ranges = [...ranges]
      .sort((a, b) => a.from - b.from)
      .map((item, index) => ({
        ...item,
        id: index + 1,
      }));
    const filterRanges = ranges.filter((item) => +item.from !== +range.from);

    const mergeArr = filterRanges.filter(
      (item) => Math.abs(item.from - range.from) === 1 || Math.abs(range.from - item.to) === 1
    );
    if (mergeArr.length && odd >= minOdd) {
      const idArr = mergeArr.map((item) => item.id);
      const mergeDotObj = {
        ...range,
        isDot: false,
      };
      ranges = filterRanges.filter((item) => !idArr.includes(item.id));
      mergeArr.forEach((item) => {
        mergeDotObj.from = item.from < mergeDotObj.from ? item.from : mergeDotObj.from;
        mergeDotObj.to = item.to < mergeDotObj.to ? mergeDotObj.to : item.to;
      });
      ranges.push(mergeDotObj);
    }
    setRangeValues(
      ranges
        .sort((a, b) => a.from - b.from)
        .map((item, index) => ({
          ...item,
          id: index + 1,
        }))
    );
  };

  const changeRangeDotValue = (e) => {
    e.stopPropagation();

    const id = +e.target.getAttribute('rangeId');
    // rangeValues.splice(id - 1, 1);
    setValidRangeValues(deepCopy(rangeValues));
    setCircleType('rangeDot');
    setSelectedRange(id);
  };

  return (
    <>
      <Range
        odd={odd}
        rangeRef={rangeRef}
        winNumber={winNumber}
        rangeValues={rangeValues}
        changeMinValue={changeMinValue}
        changeMaxValue={changeMaxValue}
        minOddOverTaken={minOddOverTaken}
        changeMaxMinValue={changeMaxMinValue}
        setValidRangeValues={setValidRangeValues}
        changeRangeDotValue={changeRangeDotValue}
        handleCurrentRangeDot={handleCurrentRangeDot}
      />
      <RangeBtnValues
        setRangeValues={setRangeValues}
        selectedRangeValues={rangeValues}
        changeRangeSelected={changeRangeSelected}
        staticRangeBtnValues={staticRangeBtnValues}
        rangeSelectedBtnValues={staticRangeBtnValues}
      />
    </>
  );
});
